import { useCallback, useEffect, useRef } from 'react';
import { useAppStore } from '../state/store';
import { buildAudioGraph, disconnectGraph, type AudioGraph } from '../audio/graph';
import { MODE_PRESETS } from '../audio/presets';
import { clampMasterGainDb, dbToLinear } from '../audio/safety';

interface AudioSession {
  audioContext: AudioContext;
  stream: MediaStream;
  graph: AudioGraph;
}

/**
 * Owns the Web Audio signal graph. Built on a user gesture (iOS requirement)
 * and torn down on stop or when the tab goes hidden.
 */
export function useAudioGraph() {
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const sessionRef = useRef<AudioSession | null>(null);
  // Synchronous guard against a second `start()` while the first is still in-flight.
  const startingRef = useRef(false);

  const masterGainDb = useAppStore((s) => s.masterGainDb);

  const setAudioState = useAppStore((s) => s.setAudioState);
  const setError = useAppStore((s) => s.setError);
  const setOutputDeviceLabel = useAppStore((s) => s.setOutputDeviceLabel);
  const setMeterDb = useAppStore((s) => s.setMeterDb);

  const stopInternal = useCallback(async () => {
    const session = sessionRef.current;
    sessionRef.current = null;

    if (audioElRef.current) {
      try {
        audioElRef.current.pause();
        audioElRef.current.srcObject = null;
      } catch {
        // ignore
      }
    }

    if (session) {
      try {
        disconnectGraph(session.graph);
      } catch {
        // ignore
      }
      try {
        session.stream.getTracks().forEach((t) => t.stop());
      } catch {
        // ignore
      }
      try {
        await session.audioContext.close();
      } catch {
        // ignore
      }
    }
  }, []);

  const start = useCallback(async () => {
    if (sessionRef.current || startingRef.current) return;
    startingRef.current = true;
    setError(null);
    setAudioState('starting');

    try {
      // iOS Safari: AudioContext must be created on a user gesture. The button
      // handler awaits this, so we are still within the user-gesture window
      // until the first await suspends. If the gesture is lost on slow
      // networks, audioEl.play() below catches the NotAllowedError.
      const AC: (typeof AudioContext) | undefined =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) {
        throw new Error('AudioContext_unsupported');
      }

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: false,
            autoGainControl: false,
            channelCount: 1,
          },
          video: false,
        });
      } catch (err) {
        if (err instanceof DOMException && err.name === 'NotAllowedError') {
          throw new Error('Permission_denied');
        }
        throw err;
      }

      const track = stream.getAudioTracks()[0];
      setOutputDeviceLabel(track?.label ?? null);
      // NOTE: low sampleRate (< 44100) likely means Bluetooth HFP —
      // later milestones surface a dedicated warning.

      const audioContext = new AC({ latencyHint: 'interactive', sampleRate: 48000 });
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      const source = audioContext.createMediaStreamSource(stream);

      const store = useAppStore.getState();
      const modePreset = MODE_PRESETS[store.mode];

      const graph = await buildAudioGraph({
        audioContext,
        source,
        initialGainDb: store.masterGainDb,
        eqCurve: modePreset.eq,
        onMeter: (rmsDb) => setMeterDb(rmsDb),
      });

      const audioEl = audioElRef.current;
      if (!audioEl) throw new Error('AudioElement_missing');
      audioEl.srcObject = graph.destination.stream;
      audioEl.muted = false;
      audioEl.volume = 1.0;
      try {
        await audioEl.play();
      } catch (err) {
        // Most likely NotAllowedError because the user gesture was lost
        // across the awaits above. Tear down and surface a Greek-localised
        // message so the next tap reuses a fresh gesture.
        if (err instanceof DOMException && err.name === 'NotAllowedError') {
          throw new Error('AudioPlayback_blocked');
        }
        throw err;
      }

      sessionRef.current = { audioContext, stream, graph };
      setAudioState('running');
    } catch (err) {
      console.error('audio start failed', err);
      const code = err instanceof Error ? err.message : String(err);
      setError(code);
      setAudioState('error');
      await stopInternal();
    } finally {
      startingRef.current = false;
    }
  }, [setAudioState, setError, setOutputDeviceLabel, setMeterDb, stopInternal]);

  const stop = useCallback(
    async (reason: 'user' | 'hidden' = 'user') => {
      await stopInternal();
      setOutputDeviceLabel(null);
      setMeterDb(null);
      // Order matters: stopInternal does NOT set audioState, so we can pick
      // the right terminal state here without a clobber.
      setAudioState(reason === 'hidden' ? 'paused' : 'idle');
    },
    [stopInternal, setAudioState, setOutputDeviceLabel, setMeterDb],
  );

  // React to master gain changes while running.
  useEffect(() => {
    const session = sessionRef.current;
    if (!session) return;
    const { masterGain } = session.graph;
    masterGain.gain.setTargetAtTime(
      dbToLinear(clampMasterGainDb(masterGainDb)),
      session.audioContext.currentTime,
      0.015,
    );
  }, [masterGainDb]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden' && sessionRef.current) {
        void stop('hidden');
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [stop]);

  useEffect(() => {
    return () => {
      void stopInternal();
    };
  }, [stopInternal]);

  const registerAudioElement = useCallback((el: HTMLAudioElement | null) => {
    audioElRef.current = el;
  }, []);

  return { start, stop, registerAudioElement };
}
